import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Layers, Loader2, CheckCircle, XCircle } from 'lucide-react';

export function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { resetPassword } = useAuth();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState('form'); // 'form' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');

    const token = searchParams.get('token');

    const getPasswordValidation = (pwd) => {
        return {
            minLength: pwd.length >= 8,
            hasUpperCase: /[A-Z]/.test(pwd),
            hasLowerCase: /[a-z]/.test(pwd),
            hasNumber: /\d/.test(pwd),
        };
    };

    const passwordValidation = getPasswordValidation(password);
    const allPasswordRequirementsMet = Object.values(passwordValidation).every(v => v);
    const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
    const passwordsDontMatch = confirmPassword.length > 0 && password !== confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            setStatus('error');
            setErrorMessage('Invalid reset link. Please request a new one.');
            return;
        }

        if (!allPasswordRequirementsMet) {
            toast.error('Please meet all password requirements');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await resetPassword(token, password);
            setStatus('success');
        } catch (err) {
            setStatus('error');
            setErrorMessage(err.message || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
                    <h1 className="text-2xl font-bold mb-2">Invalid Reset Link</h1>
                    <p className="text-muted-foreground mb-4">
                        This password reset link is invalid or has expired.
                    </p>
                    <Button onClick={() => navigate('/forgot-password')}>
                        Request new link
                    </Button>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                    <h1 className="text-2xl font-bold mb-2">Password Reset!</h1>
                    <p className="text-muted-foreground mb-4">
                        Your password has been successfully reset. You can now log in with your new password.
                    </p>
                    <Button onClick={() => navigate('/auth')}>
                        Go to login
                    </Button>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
                    <h1 className="text-2xl font-bold mb-2">Reset Failed</h1>
                    <p className="text-muted-foreground mb-4">{errorMessage}</p>
                    <div className="space-y-2">
                        <Button onClick={() => navigate('/forgot-password')} className="w-full">
                            Request new link
                        </Button>
                        <Button variant="outline" onClick={() => setStatus('form')} className="w-full">
                            Try again
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <Layers className="w-5 h-5 text-primary-foreground" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">Stacks</h1>
            </div>

            <div className="w-full max-w-sm">
                <h2 className="text-xl font-semibold mb-2">Set new password</h2>
                <p className="text-muted-foreground text-sm mb-6">
                    Enter your new password below.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="password" className="text-sm font-medium mb-1.5 block">
                            New Password
                        </Label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="8+ chars, uppercase, lowercase, number"
                            autoComplete="new-password"
                            autoFocus
                        />
                        {password.length > 0 && (
                            <div className="mt-2 space-y-1">
                                <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.minLength ? 'text-green-600' : 'text-destructive'}`}>
                                    <span>{passwordValidation.minLength ? '✓' : '✗'}</span>
                                    <span>At least 8 characters</span>
                                </div>
                                <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.hasUpperCase ? 'text-green-600' : 'text-destructive'}`}>
                                    <span>{passwordValidation.hasUpperCase ? '✓' : '✗'}</span>
                                    <span>One uppercase letter</span>
                                </div>
                                <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.hasLowerCase ? 'text-green-600' : 'text-destructive'}`}>
                                    <span>{passwordValidation.hasLowerCase ? '✓' : '✗'}</span>
                                    <span>One lowercase letter</span>
                                </div>
                                <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-destructive'}`}>
                                    <span>{passwordValidation.hasNumber ? '✓' : '✗'}</span>
                                    <span>One number</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="confirm-password" className="text-sm font-medium mb-1.5 block">
                            Confirm Password
                        </Label>
                        <Input
                            id="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter your password"
                            autoComplete="new-password"
                            className={passwordsDontMatch ? 'border-destructive' : ''}
                        />
                        {passwordsDontMatch && (
                            <p className="text-xs text-destructive mt-1.5 flex items-center gap-1">
                                <span>✗</span>
                                <span>Passwords do not match</span>
                            </p>
                        )}
                        {passwordsMatch && (
                            <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                                <span>✓</span>
                                <span>Passwords match</span>
                            </p>
                        )}
                    </div>

                    <Button
                        type="submit"
                        className="w-full h-11"
                        disabled={isLoading || !allPasswordRequirementsMet || !passwordsMatch}
                    >
                        {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            'Reset password'
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
}
